import type { FunnelPublish, FunnelSummary } from '../types/run-funnel.api.types';
import type { RunProgress } from '../types/run.api.types';

import { isRunStopped } from './run-status';

/**
 * A batch's numbers in the module's one vocabulary (design guide part 2 §2.3). `null` means the
 * read hasn't said — a caller shows "—", never 0.
 */
export interface BatchCounts {
  /** Feed rows pulled from the store (one product in one market). Never a headline. */
  listings: number | null;
  /** Distinct catalog products the batch works on — the denominator everywhere. */
  products: number | null;
  /** Products removed on purpose (filters, exclusions, already improved). */
  leftOut: number;
  /** Products a step could not process. */
  failed: number;
  /** Being worked on now. */
  inProgress: number;
  /** Products content generation wrote content for. */
  contentWritten: number | null;
  /** Passed the publish approval. */
  approved: number | null;
  /** Handed to the destination. */
  sent: number | null;
  /** The destination confirmed. */
  live: number | null;
  /** The destination turned down, or delivery failed. */
  refused: number | null;
  /** Products that never reached the end because the batch stopped. */
  stopped: number;
}

/**
 * Every user-facing product number of a batch, from its funnel summary (+ progress for the
 * fallbacks and the stopped state, + the publish read once there is one). Components read their
 * numbers here, never `productsSucceeded` (ledger rows) or raw funnel fields.
 */
export function batchCounts(
  summary: FunnelSummary | null,
  progress: RunProgress | null | undefined,
  publish?: FunnelPublish | null,
): BatchCounts {
  const stages = (summary?.stages ?? []).filter((s) => !s.sideBranch);
  const productStages = stages.filter((s) => s.unit === 'PRODUCT');
  const publishStage = (code: string) => code === 'APPROVAL' || code === 'DELIVERY';
  const sum = (pick: (s: (typeof stages)[number]) => number) =>
    productStages.reduce((total, s) => total + pick(s), 0);

  // A stage whose counts fail their own balance check would add nonsense: it's left out.
  const balanced = productStages.filter((s) => s.check !== false);
  const leftOut = balanced.reduce(
    (total, s) => total + s.excluded + (publishStage(s.stage) ? 0 : s.dropped),
    0,
  );
  const failed = sum((s) => s.failed);
  // Not-attempted products are still to come while the batch moves; a stopped batch never will.
  const moving = !isRunStopped(progress?.runStatus);
  const inProgress = sum((s) => s.inFlight + (moving ? s.notAttempted : 0));

  const products = summary?.line.products ?? progress?.totals.productsCurated ?? null;
  const liveCount = publish?.live?.confirmed ?? summary?.line.live ?? null;
  const refused =
    publish && (publish.failed !== null || publish.live)
      ? (publish.failed ?? 0) + (publish.live?.rejected ?? 0)
      : null;
  const stopped =
    isRunStopped(progress?.runStatus) && products !== null
      ? Math.max(0, products - (liveCount ?? 0) - leftOut)
      : 0;

  return {
    listings: summary?.line.itemsIn ?? progress?.totals.itemsTotal ?? null,
    products,
    leftOut,
    failed,
    inProgress,
    contentWritten: stages.find((s) => s.stage === 'ENRICHMENT')?.out ?? null,
    approved: summary?.line.ready ?? null,
    sent: publish?.sent ?? summary?.line.sent ?? null,
    live: liveCount,
    refused,
    stopped,
  };
}
